import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isFixedPageType, type TrainingAnswer, type TrainingAnswers } from '@hcc/types';
import { ApiError } from '../../../lib/api';
import {
  useCheckTrainingAnswer,
  useSaveTrainingDraft,
  useSubmitTraining,
  useTrainingDraft,
  useTrainingPlayer,
  type TrainingSubmitResult,
} from '../../../hooks/useTrainingPlayer';
import { Button } from '../../ui/Button';
import { ProgressBar } from '../../ui/ProgressBar';
import { isAnswered, pageComplete, pageQuestionCount, playerQuestions } from './answerValues';
import { ContentSlide } from './ContentSlide';
import { QuestionSlide } from './QuestionSlide';
import { QuestionGroupSlide } from './QuestionGroupSlide';
import { TextGroupSlide } from './TextGroupSlide';
import { FollowUpSlide } from './FollowUpSlide';
import { SlideNavigator } from './SlideNavigator';
import { ResultScreen } from './ResultScreen';
import { ModuleOverview } from './ModuleOverview';
import { ExternalModuleScreen } from './ExternalModuleScreen';
import { slideVideosWatched, type WatchGate } from './videoGate';

/**
 * The e-learning player: linear pages of slides and questions with
 * server-side resume (the autosave/seed/flush pattern mirrors
 * FormRenderer). full_retake grades once at the end; try_again grades each
 * question via /check and locks it when correct. Correct answers never
 * exist client-side — the result is whatever the server returns.
 */
export function ModulePlayer({
  taskId,
  backTo = '/me',
  backLabel = 'Back to your onboarding',
}: {
  taskId: string;
  /** Where the result screen's finish button goes; differs per surface. */
  backTo?: string;
  backLabel?: string;
}) {
  const player = useTrainingPlayer(taskId);
  const draft = useTrainingDraft(taskId);
  const saveDraft = useSaveTrainingDraft(taskId);
  const check = useCheckTrainingAnswer(taskId);
  const submit = useSubmitTraining(taskId);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<TrainingAnswers>({});
  const [correctIds, setCorrectIds] = useState<ReadonlySet<string>>(new Set());
  /** Watch-gated video block ids the learner has watched to threshold. */
  const [watchedBlocks, setWatchedBlocks] = useState<ReadonlySet<string>>(new Set());
  /** try_again: the last /check verdict for the current question. */
  const [lastCheck, setLastCheck] = useState<'correct' | 'incorrect' | null>(null);
  const [result, setResult] = useState<TrainingSubmitResult | null>(null);
  const [seeded, setSeeded] = useState(false);
  // Gate the pages behind the overview/resume screen until the learner begins.
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (seeded || !draft.data) return;
    setAnswers(draft.data.answers ?? {});
    setStep(draft.data.page_index ?? 0);
    setCorrectIds(new Set(draft.data.correct_question_ids ?? []));
    setWatchedBlocks(new Set(draft.data.watched_block_ids ?? []));
    setSeeded(true);
  }, [seeded, draft.data]);

  // The version the player payload displayed, sent with every write so the
  // server pins grading to it even if an admin republishes mid-attempt.
  const versionRef = useRef<string | undefined>(undefined);
  versionRef.current = player.data?.module_version_id;

  // Debounced autosave, guarded on `seeded` so the empty pre-seed state
  // never clobbers a saved draft; flush on unmount for the last edits.
  // The pending timer lives in a ref so finish()/checkCurrent can cancel
  // it — a stale autosave landing after a submit would resurrect the
  // cleared draft, and one landing after a check could clobber its result.
  const saveRef = useRef(saveDraft);
  saveRef.current = saveDraft;
  const pendingSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!seeded || result || !started) return;
    pendingSaveRef.current = setTimeout(() => {
      pendingSaveRef.current = null;
      saveRef.current.mutate({
        answers,
        page_index: step,
        watched_block_ids: [...watchedBlocks],
        module_version_id: versionRef.current,
      });
    }, 700);
    return () => {
      if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current);
      pendingSaveRef.current = null;
    };
  }, [seeded, result, started, answers, step, watchedBlocks]);
  const latestRef = useRef({ answers, page_index: step, watched_block_ids: [...watchedBlocks] });
  latestRef.current = { answers, page_index: step, watched_block_ids: [...watchedBlocks] };
  const seededRef = useRef(seeded);
  seededRef.current = seeded;
  const startedRef = useRef(started);
  startedRef.current = started;
  const submittedRef = useRef(false);
  useEffect(() => {
    return () => {
      // Only flush if they actually entered the deck; leaving from the
      // overview must not rewrite the saved checkpoint.
      if (seededRef.current && startedRef.current && !submittedRef.current) {
        saveRef.current.mutate({ ...latestRef.current, module_version_id: versionRef.current });
      }
    };
  }, []);

  if (player.isLoading || draft.isLoading || (!seeded && !draft.isError)) {
    return <p className="text-sm text-fg-muted">Loading training…</p>;
  }
  if (player.isError || !player.data || draft.isError) {
    return <p className="text-sm text-danger">Could not load this training module.</p>;
  }

  // External (redirect) modules have no pages to play — hand off to the
  // self-certify screen. Placed after the hooks + load guards so hook order is
  // stable; the quiz autosave/flush effects stay inert (started is never set).
  if (player.data.kind === 'external') {
    return <ExternalModuleScreen taskId={taskId} view={player.data} />;
  }

  const view = player.data;
  const pages = view.pages;
  const tryAgain = view.retry_mode === 'try_again';
  // Flattened: a question group contributes one entry per row, so numbering
  // and the finish gate count questions rather than pages.
  const questions = playerQuestions(pages);
  const page = pages[Math.min(step, pages.length - 1)]!;
  const currentSection = page.section_id
    ? view.sections.find((s) => s.id === page.section_id)
    : undefined;
  const questionsBefore = pages
    .slice(0, Math.min(step, pages.length - 1))
    .reduce((n, p) => n + pageQuestionCount(p), 0);
  const onThisPage = pageQuestionCount(page);
  const allDone = tryAgain
    ? questions.every((q) => correctIds.has(q.id))
    : pages.every((p) => pageComplete(p, answers, pages));
  // try_again grades one question at a time, so it only ever applies to a
  // standalone question page (publish-time validation bars it with groups).
  const singleQuestion = !isFixedPageType(page.type);
  const isLocked = tryAgain && singleQuestion && correctIds.has(page.id);

  if (result) {
    return (
      <ResultScreen
        result={result}
        taskId={taskId}
        backTo={backTo}
        backLabel={backLabel}
        onRetake={() => {
          // Re-arm the autosave/flush machinery for the fresh attempt.
          submittedRef.current = false;
          setResult(null);
          setAnswers({});
          setCorrectIds(new Set());
          setWatchedBlocks(new Set());
          setLastCheck(null);
          setStep(0);
        }}
      />
    );
  }

  // Overview / resume gate: shown on entry until the learner begins, so a
  // saved checkpoint is resumed explicitly rather than by a silent jump.
  if (!started) {
    const d = draft.data;
    const resumable =
      !!d &&
      d.source === 'draft' &&
      (Object.keys(d.answers ?? {}).length > 0 ||
        (d.page_index ?? 0) > 0 ||
        (d.correct_question_ids?.length ?? 0) > 0);
    const savedAnswers = d?.answers ?? {};
    const answered = tryAgain
      ? (d?.correct_question_ids?.length ?? 0)
      : questions.filter((q) => savedAnswers[q.id] !== undefined).length;
    return (
      <ModuleOverview
        graded={view.graded !== false}
        passMark={view.pass_mark}
        totalQuestions={questions.length}
        retryMode={view.retry_mode}
        attempts={view.attempts}
        bestScore={view.best_score}
        resume={resumable ? { answered, updatedAt: d?.updated_at ?? null } : null}
        onBegin={() => setStarted(true)}
      />
    );
  }

  function setAnswer(next: TrainingAnswer) {
    setLastCheck(null);
    setAnswers((a) => ({ ...a, [page.id]: next }));
  }

  /** Group rows answer under their own ids, not the group page id. */
  function setRowAnswer(rowId: string, optionId: string) {
    setAnswers((a) => ({ ...a, [rowId]: optionId }));
  }

  function cancelPendingSave() {
    if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current);
    pendingSaveRef.current = null;
  }

  async function checkCurrent() {
    try {
      // The check persists the answer itself when correct; a debounced
      // autosave racing it could clobber the server's verdict bookkeeping.
      cancelPendingSave();
      const res = await check.mutateAsync({
        question_id: page.id,
        answer: answers[page.id]!,
        module_version_id: versionRef.current,
      });
      setLastCheck(res.correct ? 'correct' : 'incorrect');
      if (res.correct) setCorrectIds((s) => new Set([...s, page.id]));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not check your answer');
    }
  }

  async function finish() {
    try {
      // A pending autosave landing after the submit would resurrect the
      // draft the server just cleared (a fail must restart clean).
      cancelPendingSave();
      submittedRef.current = true;
      const res = await submit.mutateAsync(
        tryAgain
          ? { module_version_id: versionRef.current }
          : { answers, module_version_id: versionRef.current },
      );
      setResult(res);
    } catch (err) {
      submittedRef.current = false;
      toast.error(err instanceof ApiError ? err.message : 'Could not submit your answers');
    }
  }

  const watchGate: WatchGate = {
    isWatched: (id) => watchedBlocks.has(id),
    onWatched: (id) => setWatchedBlocks((s) => (s.has(id) ? s : new Set(s).add(id))),
  };
  // A slide carrying an unwatched gated video holds the page until it's done.
  const videoGateUnmet = page.type === 'slide' && !slideVideosWatched(page, watchedBlocks);

  const goNext = () => {
    setLastCheck(null);
    setStep((s) => Math.min(pages.length - 1, s + 1));
  };
  // In try_again mode a question must be verified correct before moving on;
  // any page also holds while a gated video on it is unwatched.
  const nextBlocked = (tryAgain && singleQuestion && !correctIds.has(page.id)) || videoGateUnmet;

  const progressPct = Math.round(((step + 1) / pages.length) * 100);

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1.5 flex items-center justify-between text-xs text-fg-muted">
          <span>
            Page {step + 1} of {pages.length}
          </span>
          <span className="pv-num font-semibold">{progressPct}%</span>
        </div>
        <ProgressBar value={step + 1} total={pages.length} ariaLabel="Module progress" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <SlideNavigator
          pages={pages}
          step={step}
          answers={answers}
          correctIds={correctIds}
          onJump={(i) => {
            setLastCheck(null);
            setStep(i);
          }}
        />
        {onThisPage > 0 ? (
          <span className="text-xs font-medium text-fg-muted">
            {onThisPage === 1
              ? `Question ${questionsBefore + 1} of ${questions.length}`
              : `Questions ${questionsBefore + 1}-${questionsBefore + onThisPage} of ${questions.length}`}
          </span>
        ) : null}
      </div>

      {currentSection?.title ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-accent">
          {currentSection.title}
        </p>
      ) : null}

      <div className="min-h-64">
        {page.type === 'slide' ? (
          <ContentSlide slide={page} watch={watchGate} />
        ) : page.type === 'question_group' ? (
          <QuestionGroupSlide
            group={page}
            answers={answers}
            onChange={setRowAnswer}
            disabled={submit.isPending}
          />
        ) : page.type === 'text_group' ? (
          <TextGroupSlide
            group={page}
            answers={answers}
            onChange={setRowAnswer}
            disabled={submit.isPending}
          />
        ) : page.type === 'follow_up' ? (
          <FollowUpSlide
            page={page}
            pages={pages}
            answers={answers}
            onChange={setAnswer}
            disabled={submit.isPending}
          />
        ) : (
          <QuestionSlide
            question={page}
            answer={answers[page.id]}
            onChange={setAnswer}
            disabled={isLocked || submit.isPending}
          />
        )}
      </div>

      {tryAgain && singleQuestion ? (
        <div aria-live="polite">
          {isLocked ? (
            <p className="text-sm font-medium text-success-soft-fg">Correct — carry on.</p>
          ) : lastCheck === 'incorrect' ? (
            <p className="text-sm font-medium text-warning-soft-fg">
              Not quite — change your answer and try again.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2 border-t border-line/10 pt-4">
        <Button
          variant="ghost"
          disabled={step === 0}
          iconLeft={<ChevronLeft className="h-4 w-4" />}
          onClick={() => {
            setLastCheck(null);
            setStep((s) => Math.max(0, s - 1));
          }}
        >
          Previous
        </Button>
        <div className="flex items-center gap-2">
          {tryAgain && singleQuestion && !isLocked ? (
            <Button
              variant="primary"
              disabled={!isAnswered(page, answers[page.id]) || check.isPending}
              onClick={() => void checkCurrent()}
            >
              {check.isPending ? 'Checking…' : 'Submit answer'}
            </Button>
          ) : null}
          {step < pages.length - 1 ? (
            <Button
              variant="secondary"
              disabled={nextBlocked}
              iconRight={<ChevronRight className="h-4 w-4" />}
              onClick={goNext}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="success"
              disabled={!allDone || videoGateUnmet || submit.isPending}
              onClick={() => void finish()}
            >
              {submit.isPending ? 'Submitting…' : 'Finish'}
            </Button>
          )}
        </div>
      </div>
      {videoGateUnmet ? (
        <p className="text-right text-xs text-fg-muted">
          Watch the video on this slide to continue.
        </p>
      ) : step === pages.length - 1 && !allDone ? (
        <p className="text-right text-xs text-fg-muted">
          {tryAgain
            ? 'Answer every question correctly to finish.'
            : 'Answer every question before finishing — use the dots above to jump back.'}
        </p>
      ) : null}
      <p className="text-xs text-fg-dim">Your progress saves automatically.</p>
    </div>
  );
}
